import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/api";
import HolographicPlaceholder from "./HolographicPlaceholder";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, subtotalCents } = useCart();
  const nav = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    nav("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        side="right"
        className="bg-[#0A0812] border-l border-white/10 text-white w-full sm:max-w-md p-0 flex flex-col"
      >
        <SheetHeader className="px-6 py-5 border-b border-white/5">
          <SheetTitle className="text-white font-display text-2xl flex items-center gap-2">
            <ShoppingBag size={20} className="text-[#B026FF]" /> Your Cart
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-20">
              <div className="text-white/40 font-mono text-xs tracking-[0.3em] uppercase">Empty</div>
              <div className="mt-3 text-white/70">Your cart is waiting for some chase pulls.</div>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((it) => (
                <li key={it.product_id} className="flex gap-4 items-start" data-testid={`cart-item-${it.slug}`}>
                  <div className="w-20 shrink-0">
                    <HolographicPlaceholder label={it.name} ratio="aspect-[3/4]" imageUrl={it.image_url} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#B026FF]/80 font-bold">
                      {it.category?.replace(/-/g, " ")}
                    </div>
                    <div className="text-sm text-white font-medium truncate">{it.name}</div>
                    <div className="font-mono text-sm text-white mt-1">{formatPrice(it.price_cents)}</div>

                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex items-center border border-white/10 rounded-md">
                        <button
                          onClick={() => setQty(it.product_id, it.quantity - 1)}
                          data-testid={`cart-qty-decr-${it.slug}`}
                          className="px-2 py-1 text-white/70 hover:text-white"
                          aria-label="Decrease quantity"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="px-2 font-mono text-sm">{it.quantity}</span>
                        <button
                          onClick={() => setQty(it.product_id, it.quantity + 1)}
                          data-testid={`cart-qty-incr-${it.slug}`}
                          className="px-2 py-1 text-white/70 hover:text-white"
                          aria-label="Increase quantity"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <button
                        onClick={() => remove(it.product_id)}
                        data-testid={`cart-remove-${it.slug}`}
                        className="text-white/40 hover:text-red-400 ml-auto"
                        aria-label={`Remove ${it.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-white/5 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-white/60">Subtotal</span>
            <span className="font-mono text-lg text-white">{formatPrice(subtotalCents)}</span>
          </div>
          <button
            onClick={goCheckout}
            disabled={items.length === 0}
            data-testid="cart-checkout-btn"
            className="btn-violet w-full h-12 rounded-md font-medium tracking-wide disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Checkout →
          </button>
          <div className="text-[11px] tracking-[0.22em] uppercase text-white/40 text-center font-mono">
            Free shipping over £50 • 30-day returns
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
